import React, { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PilotCouncilConfig } from './pilotTypes'
import pilotHttp from './pilotHttp'

interface PilotSessionContextType {
  council: string
  sessionToken: string
  config: PilotCouncilConfig | null
  configLoading: boolean
}

const PilotSessionContext = createContext<PilotSessionContextType | undefined>(undefined)

export const PilotSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { council = '' } = useParams<{ council: string }>()

  // Session token: generate once per council, persist in localStorage
  const [sessionToken] = useState<string>(() => {
    const key = `pilot_session_${council}`
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const newToken = crypto.randomUUID()
    localStorage.setItem(key, newToken)
    return newToken
  })

  const [config, setConfig] = useState<PilotCouncilConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(true)

  useEffect(() => {
    if (!council) return
    pilotHttp
      .get(`/api/pilot/${council}/config/`)
      .then(res => setConfig(res.data))
      .catch(() => setConfig(null))
      .finally(() => setConfigLoading(false))
  }, [council])

  return (
    <PilotSessionContext.Provider value={{ council, sessionToken, config, configLoading }}>
      {children}
    </PilotSessionContext.Provider>
  )
}

export const usePilotSession = () => {
  const ctx = useContext(PilotSessionContext)
  if (!ctx) throw new Error('usePilotSession must be used within PilotSessionProvider')
  return ctx
}
